// src/prf/prfQuoteDraft.js
// A PRF quote before — and whether or not — it is ever saved.
//
// ═══════════════════════════════════════════════════════════════════
// THE UNSAVED QUOTE IS THE PRIMARY OBJECT, NOT A DRAFT OF A ROW
// ═══════════════════════════════════════════════════════════════════
//
// An agent must be able to quote somebody who has never existed in this system
// — no farmer record, no row, nothing written — and get a real number. So the
// draft is a plain value here, `priceDraft()` prices it with no database at
// all, and `draftToRows()` is a separate step that only runs when a human
// chooses to save. Nothing in the pricing path touches persistence, which is
// what keeps "quote a prospect without saving" true by construction rather
// than by remembering not to write.
//
// ⚠ AND THE DOLLARS ARE NEVER THE RECORD. `draftToRows()` emits the INPUT SET
// plus `inputs_fingerprint`, `engine_version` and `adm_vintage`; the priced
// figures ride along as a cache that can be recomputed and checked. sql/050
// says the same thing in its own comments — this is the code half of it.
//
// ⚠ PHYSICAL EXPOSURE NEVER BECOMES THE INSURED ASSIGNMENT. `exposure` is what
// was measured; `insured_grid_id` is what a human chose. `assignmentReady()`
// refuses a block that has not been assigned, and there is deliberately no
// function here that fills it in from the majority grid — 612 fields in this
// book straddle a line, and picking the bigger half would put acres on an
// application nobody elected.

const { quotePrf, overlappingIntervals, overlapMessage, bfrBonusForYear } = require("./prfQuote.js");
const { LAB } = require("./prfOptimizer.js");

const ENGINE_VERSION = "prfQuote@2026-08-25";

// ── an empty draft ──────────────────────────────────────────────────
function newDraft({ cropYear, stateCode = "36" }) {
  return {
    subject: { farmer_id: null, prospect_name: "", prospect_contact: "" },
    crop_year: Number(cropYear),
    state_code: String(stateCode),
    coverage_level: 0.85,
    productivity_factor: 1.0,
    // Beginning farmer and rancher crop year, 1..10, or null for not a BFR.
    // MGR-25-006: +15 subsidy points in years 1-2, +13 in 3, +11 in 4, +10 in 5-10.
    bfr_year: null,
    blocks: [],
  };
}

function newBlock({ label = "", acres = null, exposure = null } = {}) {
  return {
    key: `b${Math.floor(performance?.now?.() ?? 0)}_${Math.floor(Math.random() * 1e6)}`,
    label,
    acres: acres == null ? null : Number(acres),
    share: 1,
    // MEASURED — where the ground lies. Set by gridExposure(); never edited.
    exposure,
    // CHOSEN — what the application insures. Null until a human says.
    insured_grid_id: null,
    county_code: null,
    intended_use: null,     // HAYING | GRAZING
    practice_code: null,
    type_code: null,
    allocation: {},         // interval -> percent of value
  };
}

// ── what still has to be answered before this can price ─────────────
// Every gate names ITSELF, because "cannot price" with no reason is the
// generic-refusal failure this repo has already fixed once on the PRF screen.
function blockGaps(block) {
  const gaps = [];
  if (!(Number(block.acres) > 0)) gaps.push({ field: "acres", says: "No acreage yet — draw the ground or type the acres." });
  if (block.insured_grid_id == null) {
    gaps.push({
      field: "insured_grid_id",
      says: block.exposure?.straddles
        ? `These acres cross ${block.exposure.grids.length} grids. Choose the grid this block is INSURED in — it is not split automatically.`
        : "Choose the grid this block is insured in.",
    });
  }
  if (!block.county_code) gaps.push({ field: "county_code", says: "Choose the county — the county base value and the premium rate are both per county." });
  if (!block.intended_use) gaps.push({ field: "intended_use", says: "Choose haying or grazing — they carry different county base values." });
  const total = allocationTotal(block);
  const elected = Object.keys(block.allocation || {}).filter((k) => Number(block.allocation[k]) > 0);
  if (!elected.length) {
    gaps.push({ field: "allocation", says: "No intervals elected yet." });
  } else {
    // RMA requires at least TWO index intervals — `quotePrf` throws on one, so
    // the gate is stated here rather than surfacing as an exception.
    if (elected.length < 2) {
      gaps.push({ field: "allocation", says: "RMA requires at least two index intervals — one interval cannot be insured alone." });
    }
    if (Math.abs(total - 1) > 0.005) {
      gaps.push({ field: "allocation", says: `Interval percentages total ${Math.round(total * 100)}% — RMA requires exactly 100%.` });
    }
    // Overlapping intervals — the SAME rule quotePrf refuses on, said here so it
    // arrives as an explanation rather than as a thrown exception. One copy of
    // the rule, imported; two would drift.
    const overlaps = overlappingIntervals(block.allocation || {});
    if (overlaps.length) {
      gaps.push({ field: "allocation", says: overlapMessage(overlaps) });
    }
  }
  return gaps;
}

function allocationTotal(block) {
  return Object.keys(block.allocation || {})
    .reduce((s, k) => s + (Number(block.allocation[k]) || 0), 0);
}

// A block whose grid has been chosen. Separate from `blockGaps` so a screen can
// ask specifically "has a human made the assignment", which is the one decision
// this module refuses to make on their behalf.
function assignmentReady(block) {
  return block.insured_grid_id != null && !!block.county_code && !!block.intended_use;
}

function draftGaps(draft) {
  const gaps = [];
  const named = String(draft.subject?.prospect_name || "").trim();
  if (!draft.subject?.farmer_id && !named) {
    gaps.push({ field: "subject", says: "Name the prospect, or attach the quote to a customer." });
  }
  if (!draft.blocks?.length) gaps.push({ field: "blocks", says: "No ground on the quote yet." });
  draft.blocks?.forEach((b, i) => {
    for (const g of blockGaps(b)) gaps.push({ ...g, block: i, block_label: b.label || `Block ${i + 1}` });
  });
  return gaps;
}

// ── pricing: the SAME engine, no database ───────────────────────────
// `refs` supplies what only the caller can know — the CBV and the rates for the
// block's own identity. Nothing is looked up here, so this function is pure and
// a checker can drive it without a network.
function priceBlock(block, draft, refs) {
  const gaps = blockGaps(block);
  if (gaps.length) return { ok: false, gaps };
  if (!(Number(refs?.cbv) > 0)) {
    // ⚠ "RMA PUBLISHES NONE" AND "IT VARIES AND YOU HAVE NOT PICKED" ARE
    // DIFFERENT ANSWERS, and only one of them is the agent's to fix. Saying the
    // first when the second is true tells an agent the actuarials are missing
    // while the practice dropdown sits unanswered beside it — measured on grid
    // 27811 / county 055, where haying publishes two values by practice.
    // A STATE COLLISION IS A THIRD ANSWER AGAIN, NOT A PRACTICE ONE.
    // A PRF grid straddles state lines and a county CODE repeats across states,
    // so a border grid can surface two states' county base values under one
    // county code, with identical practice codes in both. Choosing a practice
    // cannot resolve that - it would just pick whichever state sorted first -
    // so it is refused by its own name. Measured 2026-09-01: grids 26613/26614
    // county 015 span NY and PA; no grid in the book is affected.
    if (refs?.cbv_multi_state) {
      return { ok: false, error: "PRF_STATE_AMBIGUOUS",
               says: "This grid crosses a state line and RMA publishes a different county base value on each side under the same county code. Silas cannot tell which state this ground is in, so it will not price it." };
    }
    if (refs?.cbv_varies) {
      return { ok: false, error: "PRF_PRACTICE_REQUIRED",
               says: "RMA publishes more than one county base value here, by practice. Choose the practice above — they are not averaged." };
    }
    return { ok: false, error: "PRF_CBV_UNRESOLVED",
             says: "RMA publishes no county base value for this grid, county and intended use." };
  }
  const missing = Object.keys(block.allocation)
    .filter((l) => block.allocation[l] > 0 && !(Number(refs?.rates?.[l]) > 0));
  if (missing.length) {
    return { ok: false, error: "NO_RATE", intervals: missing,
             says: `RMA premium rates are not loaded for ${missing.join(", ")} on this grid, county and crop year.` };
  }
  // ⚠ quotePrf THROWS on an allocation RMA would not accept — under 100%, one
  // interval, a productivity factor outside 60–150%, or a percent-of-value
  // outside the county's own limits. The gates above catch what a screen can
  // explain; this catch turns anything left into a named refusal rather than an
  // exception reaching the render.
  let q;
  try {
    q = quotePrf({
      acres: Number(block.acres),
      share: Number(block.share) || 1,
      countyBaseValue: Number(refs.cbv),
      coverageLevel: Number(draft.coverage_level),
      productivityFactor: Number(draft.productivity_factor),
      allocation: block.allocation,
      rates: refs.rates,
      // Supplied only when RMA's own limits are loaded. Without them the quote
      // is still priced and is labelled unverified — the same fail-closed
      // posture the review screen already takes.
      rules: refs.limits || undefined,
      bfrBonus: bfrBonusForYear(draft.bfr_year ?? null),
    });
  } catch (e) {
    return { ok: false, error: "REFUSED_BY_ENGINE", says: e?.message || String(e) };
  }
  // A dropped interval makes a quote read LOW, and a low quote reads as good
  // news, so it is carried out rather than swallowed.
  return { ok: true, quote: q, incomplete: q.incomplete || [] };
}

function priceDraft(draft, refsByBlockKey) {
  const blocks = (draft.blocks || []).map((b) => ({
    key: b.key, label: b.label,
    result: priceBlock(b, draft, refsByBlockKey?.[b.key] || null),
  }));
  const priced = blocks.filter((b) => b.result.ok);
  const sum = (f) => priced.reduce((s, b) => s + (Number(b.result.quote?.[f]) || 0), 0);
  // ⚠ THE TOTAL IS THE SUM OF THE PRICED BLOCK LINES AND NOTHING ELSE, and what
  // it LEAVES OUT is carried beside it in ACRES as well as blocks. "3 of 4
  // blocks" understates the hole when the missing one is the home farm: a
  // reader needs the acreage to know whether the number in front of them is
  // nearly the whole quote or a third of it. A partial total that looks complete
  // is the failure this repo keeps recording in other shapes.
  const acresOf = (key) => Number((draft.blocks || []).find((b) => b.key === key)?.acres) || 0;
  const pricedAcres = priced.reduce((s, b) => s + acresOf(b.key), 0);
  const allAcres = (draft.blocks || []).reduce((s, b) => s + (Number(b.acres) || 0), 0);

  return {
    blocks,
    priced_blocks: priced.length,
    unpriced_blocks: blocks.length - priced.length,
    priced_acres: pricedAcres,
    excluded_acres: allAcres - pricedAcres,
    total_acres: allAcres,
    protection: sum("total_protection"),
    total_premium: sum("total_premium"),
    subsidy: sum("total_subsidy"),
    producer_premium: sum("producer_premium"),
    complete: blocks.length > 0 && blocks.every((b) => b.result.ok),
  };
}

// ── the fingerprint ─────────────────────────────────────────────────
// A canonical, order-independent rendering of everything that decides the
// price. Stored beside the cached dollars so a re-price that disagrees can say
// WHICH input moved instead of just disagreeing.
function inputsFingerprint(draft) {
  const canon = {
    crop_year: draft.crop_year,
    state_code: draft.state_code,
    coverage_level: round4(draft.coverage_level),
    productivity_factor: round4(draft.productivity_factor),
    // Only when set, so every fingerprint saved before BFR existed still matches.
    ...(draft.bfr_year != null ? { bfr_year: Number(draft.bfr_year) } : {}),
    blocks: (draft.blocks || []).map((b) => ({
      grid: b.insured_grid_id, county: b.county_code, use: b.intended_use,
      practice: b.practice_code ?? null, type: b.type_code ?? null,
      acres: round4(b.acres), share: round4(b.share),
      alloc: LAB.filter((l) => (b.allocation || {})[l] > 0)
                .map((l) => `${l}:${round4(b.allocation[l])}`).join(","),
    })).sort((x, y) => JSON.stringify(x).localeCompare(JSON.stringify(y))),
  };
  return "fp1:" + hash32(JSON.stringify(canon));
}

function round4(x) { const n = Number(x); return Number.isFinite(n) ? Math.round(n * 1e4) / 1e4 : null; }

// FNV-1a. Not a security hash — an equality token for "are these the same
// inputs", which is all a fingerprint has to answer here.
function hash32(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return ("00000000" + h.toString(16)).slice(-8) + ":" + s.length;
}

// ── saving, only when a human asks ──────────────────────────────────
// Returns the rows sql/050 expects. It does NOT write — the caller does, so
// this stays pure and testable and there is exactly one place that can create a
// quote row.
function draftToRows(draft, pricing, { admVintage, presented = false }) {
  const quote = {
    farmer_id: draft.subject?.farmer_id || null,
    prospect_name: draft.subject?.farmer_id ? null : (String(draft.subject?.prospect_name || "").trim() || null),
    prospect_contact: draft.subject?.prospect_contact || null,
    crop_year: draft.crop_year,
    state_code: draft.state_code,
    coverage_level: draft.coverage_level,
    productivity_factor: draft.productivity_factor,
    // ⚠ ONLY WHEN SET. prf_quotes.bfr_year arrives with the SQL in
    // silas/sql-to-run; a key PostgREST does not know refuses the whole insert,
    // so a non-BFR quote never sends it and saves exactly as before.
    ...(draft.bfr_year != null ? { bfr_year: Number(draft.bfr_year) } : {}),
    engine_version: ENGINE_VERSION,
    adm_vintage: admVintage,
    inputs_fingerprint: inputsFingerprint(draft),
    status: presented ? "presented" : "draft",
    presented_at: presented ? new Date().toISOString() : null,
  };
  const blocks = [], exposure = [], intervals = [];
  for (const b of draft.blocks || []) {
    const priced = (pricing?.blocks || []).find((p) => p.key === b.key);
    const q = priced?.result?.ok ? priced.result.quote : null;
    blocks.push({
      _key: b.key,
      block_label: b.label || null,
      insured_grid_id: b.insured_grid_id,
      county_code: b.county_code,
      intended_use: b.intended_use,
      practice_code: b.practice_code || null,
      type_code: b.type_code,
      acres: b.acres, share: b.share,
      cbv_per_acre: q ? q.county_base_value ?? null : null,
      cbv_source: b.cbv_source || null,
    });
    // The MEASUREMENT, stored beside the decision and never merged into it.
    for (const g of (b.exposure?.grids || [])) {
      exposure.push({ _key: b.key, grid_id: g.grid, acres: g.acres, share: g.share,
                      method: b.exposure.method, geom: b.geom || null });
    }
    for (const l of LAB) {
      const pct = (b.allocation || {})[l];
      if (!(pct > 0)) continue;
      const iv = (q?.lines || []).find((x) => x.interval === l) || null;
      intervals.push({
        _key: b.key, interval_code: l, percent_of_value: pct,
        base_rate: iv?.base_rate ?? null,
        protection: iv?.protection ?? null,
        total_premium: iv?.total_premium ?? null,
        subsidy: iv?.premium_subsidy ?? null,
        producer_premium: iv?.producer_premium ?? null,
      });
    }
  }
  return { quote, blocks, exposure, intervals };
}

// ── reopening a saved quote ─────────────────────────────────────────
// The inverse of draftToRows: stored rows back into a draft, so a saved quote is
// RE-PRICED from its inputs rather than read back from its cached dollars.
//
// ⚠ THIS IS THE WHOLE POINT OF STORING INPUTS. If reopening a quote showed the
// stored `producer_premium` column, a quote could reopen with figures its own
// inputs no longer produce and nothing would ever say so. Regenerating instead
// means a disagreement is visible: same inputs and same engine must give the
// same number, and if they do not, either the engine moved (`engine_version`)
// or the actuarials did (`adm_vintage`) — both of which the row records.
//
// It deliberately does NOT restore the drawn geometry into `exposure` from the
// cached acres. The exposure rows carry the MEASUREMENT; rebuilding a polygon
// from them would invent a boundary. They are restored as they were stored.
function rowsToDraft({ quote, blocks, exposure, intervals }) {
  const draft = {
    subject: {
      farmer_id: quote.farmer_id || null,
      prospect_name: quote.prospect_name || "",
      prospect_contact: quote.prospect_contact || "",
    },
    crop_year: Number(quote.crop_year),
    state_code: String(quote.state_code),
    coverage_level: Number(quote.coverage_level),
    productivity_factor: Number(quote.productivity_factor),
    bfr_year: quote.bfr_year == null ? null : Number(quote.bfr_year),
    blocks: [],
  };
  for (const b of blocks || []) {
    const exp = (exposure || []).filter((e) => e.block_id === b.id);
    const total = exp.reduce((s, e) => s + (Number(e.acres) || 0), 0);
    const alloc = {};
    for (const iv of (intervals || []).filter((i) => i.block_id === b.id)) {
      alloc[iv.interval_code] = Number(iv.percent_of_value);
    }
    draft.blocks.push({
      key: b.id,
      label: b.block_label || "",
      acres: Number(b.acres),
      share: Number(b.share) || 1,
      exposure: exp.length ? {
        total_acres: total,
        grids: exp.map((e) => ({ grid: Number(e.grid_id), acres: Number(e.acres), share: Number(e.share) }))
                  .sort((x, y) => y.acres - x.acres || x.grid - y.grid),
        straddles: exp.length > 1,
        method: exp[0].method,
      } : null,
      insured_grid_id: b.insured_grid_id == null ? null : Number(b.insured_grid_id),
      county_code: b.county_code || null,
      intended_use: b.intended_use || null,
      practice_code: b.practice_code || null,
      type_code: b.type_code || null,
      cbv_source: b.cbv_source || null,
      allocation: alloc,
    });
  }
  return draft;
}

module.exports = {
  ENGINE_VERSION, newDraft, newBlock, rowsToDraft,
  blockGaps, draftGaps, allocationTotal, assignmentReady,
  priceBlock, priceDraft, inputsFingerprint, draftToRows,
};
